import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AlertSummary, AlertFilters, HazardType, Severity } from '../types/cap'
import { AU_STATES } from '../constants/auStates'

interface AlertState {
  alerts: AlertSummary[]
  selectedAlertId: string | null
  filters: AlertFilters
  dismissedIds: Set<string>
  liveConnected: boolean
  flyTo: ((lon: number, lat: number, zoom?: number) => void) | null
  fitBounds: ((bbox: [number, number, number, number], padding?: number) => void) | null

  // Actions
  setAlerts: (alerts: AlertSummary[]) => void
  upsertAlert: (alert: AlertSummary) => void
  cancelAlert: (id: string) => void
  selectAlert: (id: string | null) => void
  setFilters: (filters: Partial<AlertFilters>) => void
  toggleHazardType: (hazard: HazardType) => void
  toggleSeverity: (severity: Severity) => void
  dismissAlert: (id: string) => void
  setLiveConnected: (connected: boolean) => void
  registerFlyTo: (fn: (lon: number, lat: number, zoom?: number) => void) => void
  registerFitBounds: (fn: (bbox: [number, number, number, number], padding?: number) => void) => void
}

const DEFAULT_FILTERS: AlertFilters = {
  hazard_types: [],
  severity: [],
  active_only: true,
  min_priority: 1,
  search: '',
  region: null,
}

export const useAlertStore = create<AlertState>()(
  persist(
    (set) => ({
  alerts: [],
  selectedAlertId: null,
  filters: DEFAULT_FILTERS,
  dismissedIds: new Set<string>(),
  liveConnected: false,
  flyTo: null,
  fitBounds: null,

  setAlerts: (alerts) => set({ alerts }),
  registerFlyTo: (fn) => set({ flyTo: fn }),
  registerFitBounds: (fn) => set({ fitBounds: fn }),

  upsertAlert: (alert) =>
    set((state) => {
      const idx = state.alerts.findIndex((a) => a.id === alert.id)
      if (idx >= 0) {
        const next = [...state.alerts]
        next[idx] = alert
        return { alerts: next }
      }
      return { alerts: [alert, ...state.alerts] }
    }),

  cancelAlert: (id) =>
    set((state) => ({
      alerts: state.alerts.filter((a) => a.id !== id),
      selectedAlertId: state.selectedAlertId === id ? null : state.selectedAlertId,
    })),

  selectAlert: (id) => set({ selectedAlertId: id }),

  setFilters: (partial) =>
    set((state) => ({ filters: { ...state.filters, ...partial } })),

  toggleHazardType: (hazard) =>
    set((state) => {
      const current = state.filters.hazard_types
      const next = current.includes(hazard)
        ? current.filter((h) => h !== hazard)
        : [...current, hazard]
      return { filters: { ...state.filters, hazard_types: next } }
    }),

  toggleSeverity: (severity) =>
    set((state) => {
      const current = state.filters.severity
      const next = current.includes(severity)
        ? current.filter((s) => s !== severity)
        : [...current, severity]
      return { filters: { ...state.filters, severity: next } }
    }),

  dismissAlert: (id) =>
    set((state) => ({
      dismissedIds: new Set([...state.dismissedIds, id]),
      selectedAlertId: state.selectedAlertId === id ? null : state.selectedAlertId,
    })),

  setLiveConnected: (connected) => set({ liveConnected: connected }),
    }),
    {
      name: 'cap-alert-filters',
      // Persist filter choices only — search is session-only, runtime state never serialised
      partialize: (state) => ({ filters: { ...state.filters, search: '' } }),
    }
  )
)

// Derived selector — apply client-side filters to the alert list
function sortAlerts(alerts: AlertSummary[]): AlertSummary[] {
  const now = Date.now()
  return [...alerts].sort((a, b) => {
    const pa = a.priority_score ?? 0
    const pb = b.priority_score ?? 0
    if (pb !== pa) return pb - pa
    const ae = a.expires_at ? new Date(a.expires_at).getTime() - now : Infinity
    const be = b.expires_at ? new Date(b.expires_at).getTime() - now : Infinity
    if (ae !== be) return ae - be
    return new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime()
  })
}

export function selectFilteredAlerts(state: AlertState): AlertSummary[] {
  const { alerts, filters, dismissedIds } = state
  const regionBbox = filters.region
    ? AU_STATES.find((s) => s.code === filters.region)?.bbox
    : undefined
  return sortAlerts(alerts.filter((alert) => {
    if (dismissedIds.has(alert.id)) return false
    if (filters.hazard_types.length > 0 && !filters.hazard_types.includes(alert.hazard_type)) {
      return false
    }
    if (filters.severity.length > 0 && alert.severity && !filters.severity.includes(alert.severity)) {
      return false
    }
    if (alert.priority_score < filters.min_priority) {
      return false
    }
    if (filters.active_only && alert.expires_at && new Date(alert.expires_at) < new Date()) {
      return false
    }
    if (regionBbox) {
      if (!alert.centroid) return false
      const [lon, lat] = alert.centroid.coordinates
      const [minLon, minLat, maxLon, maxLat] = regionBbox
      if (lon < minLon || lon > maxLon || lat < minLat || lat > maxLat) return false
    }
    if (filters.search) {
      const q = filters.search.toLowerCase()
      return (
        alert.headline?.toLowerCase().includes(q) ||
        alert.region_name?.toLowerCase().includes(q) ||
        alert.hazard_type.includes(q)
      )
    }
    return true
  }))
}
